/**
 * components/procurement/SiteLinkPicker.jsx
 *
 * Searchable multi-select that links a contract to existing UBM master data
 * (properties / vendor accounts / meters). No duplicate master data is ever
 * created — we only reference existing records by id.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Search, X, Plus, MapPin, Building, Gauge } from 'lucide-react';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { searchSites } from '../../api/contracts';

const KIND_META = {
  property: { icon: MapPin, label: 'Properties', placeholder: 'Search properties by name / address…' },
  account: { icon: Building, label: 'Utility Accounts', placeholder: 'Search by account #, vendor, site…' },
  meter: { icon: Gauge, label: 'Meters', placeholder: 'Search by meter number…' },
};

const labelFor = (kind, item) => {
  if (kind === 'property') return `${item.name || 'Unnamed'}${item.city ? ` · ${item.city}, ${item.state || ''}` : ''}`;
  if (kind === 'account') return `${item.account_number || '—'} · ${item.vendor_name || ''}${item.site_name ? ` (${item.site_name})` : ''}`;
  return `${item.meter_number || '—'}${item.commodity ? ` · ${item.commodity}` : ''}`;
};

export function SiteLinkPicker({ accountId, kind, commodity, selected = [], selectedLabels = {}, onChange }) {
  const meta = KIND_META[kind];
  const Icon = meta.icon;
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [labels, setLabels] = useState(selectedLabels);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef();

  useEffect(() => { setLabels((prev) => ({ ...selectedLabels, ...prev })); }, [selectedLabels]);

  useEffect(() => {
    if (!open) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      if (!accountId) return;
      setLoading(true);
      try {
        const data = await searchSites({ accountId, kind, q, commodity });
        setResults(data || []);
      } catch { setResults([]); } finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(timer.current);
  }, [q, open, accountId, kind, commodity]);

  const add = (item) => {
    if (selected.includes(item.id)) return;
    setLabels((l) => ({ ...l, [item.id]: labelFor(kind, item) }));
    onChange([...selected, item.id]);
    setQ('');
  };
  const remove = (id) => onChange(selected.filter((s) => s !== id));

  return (
    <div className="space-y-2" data-testid={`site-picker-${kind}`}>
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <Icon className="w-3.5 h-3.5" /> {meta.label}
        {selected.length > 0 && <span className="text-primary">({selected.length})</span>}
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((id) => (
            <Badge key={id} variant="secondary" className="gap-1 pr-1 max-w-full" data-testid={`site-chip-${id}`}>
              <span className="truncate max-w-[220px]">{labels[id] || id}</span>
              <button type="button" onClick={() => remove(id)} className="hover:text-destructive" data-testid={`site-remove-${id}`}>
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-muted-foreground" />
        <Input
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          placeholder={meta.placeholder}
          className="pl-8"
          data-testid={`site-search-${kind}`}
        />
        {open && (
          <div className="absolute z-50 mt-1 w-full bg-popover border rounded-md shadow-lg max-h-60 overflow-auto">
            <div className="flex justify-between items-center px-2 py-1 border-b">
              <span className="text-xs text-muted-foreground">{loading ? 'Searching…' : `${results.length} result(s)`}</span>
              <button type="button" className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setOpen(false)}>Close</button>
            </div>
            {results.length === 0 && !loading && (
              <div className="px-3 py-4 text-sm text-muted-foreground text-center">No matches</div>
            )}
            {results.map((item) => {
              const picked = selected.includes(item.id);
              return (
                <button
                  type="button"
                  key={item.id}
                  onClick={() => add(item)}
                  disabled={picked}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-muted ${picked ? 'opacity-50' : ''}`}
                  data-testid={`site-option-${item.id}`}
                >
                  <span className="truncate">{labelFor(kind, item)}</span>
                  {picked ? <span className="text-xs text-primary">Added</span> : <Plus className="w-3.5 h-3.5 text-muted-foreground" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default SiteLinkPicker;
